import React from 'react';
import { Platform, View, Text, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import colors from '@/constants/Colors';
import { isOneSignalAvailable } from '@/lib/onesignal-push';
import { usePushRegistration } from '@/context/PushRegistrationProvider';

function pushIsDeliverable(
    localState: ReturnType<typeof usePushRegistration>['localState'],
    locallyHealthy: boolean,
    isFullyHealthy: boolean,
): boolean {
    if (locallyHealthy || isFullyHealthy) return true
    return Boolean(localState.optedIn && localState.subscriptionId)
}

export default function PushPermissionBanner() {
    const router = useRouter();
    const insets = useSafeAreaInsets();
    const { localState, locallyHealthy, isFullyHealthy, isHealing, lastHealedAt } = usePushRegistration()

    if (Platform.OS === 'web' || !isOneSignalAvailable()) return null

    // Wait for the first health check so we don't flash the banner on startup.
    if (lastHealedAt === null || isHealing) return null

    if (pushIsDeliverable(localState, locallyHealthy, isFullyHealthy)) return null

    const { permission } = localState

    // Only prompt when OS permission is genuinely blocked and push is not working.
    if (permission) return null

    const handleOpenGuide = () => {
        try {
            router.push('/enable-notifications');
        } catch {
            // If the route isn't available (e.g. not signed in), do nothing.
        }
    };

    return (
        <TouchableOpacity
            onPress={handleOpenGuide}
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel="Push notifications are disabled. Tap to fix."
            style={{
                width: '100%',
                backgroundColor: colors.error,
                paddingTop: insets.top,
                borderBottomColor: 'rgba(0,0,0,0.1)',
                borderBottomWidth: 1,
            }}
        >
            <View
                style={{
                    paddingHorizontal: 16,
                    paddingVertical: 10,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <View style={{ flex: 1, paddingRight: 12 }}>
                    <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }} numberOfLines={1}>
                        Push notifications are off
                    </Text>
                    <Text
                        style={{ color: '#fff', opacity: 0.9, fontSize: 12, marginTop: 2 }}
                        numberOfLines={2}
                    >
                        Allow notifications so you don&apos;t miss job alerts, messages and updates.
                    </Text>
                </View>
                <View
                    style={{
                        backgroundColor: colors.accent,
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                        borderRadius: 6,
                    }}
                >
                    <Text style={{ color: '#000', fontWeight: '700', fontSize: 12 }}>Fix it</Text>
                </View>
            </View>
        </TouchableOpacity>
    );
}
